// Deterministic family-history risk engine — parses the free-text FAMILY
// HISTORY field into screening/risk insight cards. Family history is kept
// OUT of the patient's own condition logic (a father's diabetes must not
// trigger diabetic dosing rules) — it drives screening advice instead.
// interval_months = how long a "done" tick silences the advice before it
// automatically becomes due again.

const FH_RULES = [
  {
    id: "fh-dm",
    re: /diabet|madhumeh|\bdm\b|\bsugar\b/,
    name: "Diabetes in the family",
    note: "First-degree family history confers a 2–4× lifetime risk of type 2 diabetes.",
    actions: "If not already diabetic: annual HbA1c / FPG from age 25–30; weight & lifestyle counselling now; advise screening for siblings/children too.",
    interval_months: 12,
  },
  {
    id: "fh-cad",
    re: /heart attack|\bmi\b|\bcad\b|\bihd\b|angioplasty|bypass|cabg|stent|cardiac arrest|heart disease/,
    name: "Family history of coronary artery disease",
    note: "Premature CAD in a first-degree relative (male <55 y / female <65 y) is an independent ASCVD risk-enhancer.",
    actions: "Lipid profile now + one-time Lp(a); apply risk-enhancer logic to statin threshold; strict BP and tobacco counselling.",
    interval_months: 12,
  },
  {
    id: "fh-stroke",
    re: /stroke|paralysis|\bcva\b|brain attack/,
    name: "Family history of stroke",
    note: "Adds to cerebrovascular risk, especially with hypertension or AF.",
    actions: "Tight BP control target; check pulse for AF at every visit; lipids and smoking counselling.",
    interval_months: 12,
  },
  {
    id: "fh-htn",
    re: /hypertension|blood pressure|\bbp\b|\bhtn\b/,
    name: "Hypertension in the family",
    note: "Strong heritability — offspring risk roughly doubles.",
    actions: "If normotensive: BP check at every visit + home readings yearly; early salt-restriction counselling.",
    interval_months: 12,
  },
  {
    id: "fh-breast",
    re: /breast cancer|ovarian cancer|ovary cancer/,
    name: "Familial breast / ovarian cancer",
    note: "First-degree history raises risk ~2×; multiple/young relatives suggest a hereditary syndrome.",
    actions: "Clinical breast exam; start mammography 10 years before the youngest affected relative's age (or at 40); consider genetics referral if ≥2 relatives or onset <45 y.",
    interval_months: 12,
  },
  {
    id: "fh-crc",
    re: /colon cancer|colorectal|bowel cancer|rectal cancer/,
    name: "Familial colorectal cancer",
    note: "One first-degree relative roughly doubles risk.",
    actions: "Colonoscopy at age 40, or 10 years before the youngest affected relative — whichever is earlier; report any bleeding PR promptly.",
    interval_months: 60,
  },
  {
    id: "fh-thyroid",
    re: /thyroid/,
    name: "Thyroid disease in the family",
    note: "Autoimmune thyroid disease clusters strongly in families.",
    actions: "Check TSH if never done (and in any pregnancy planning); examine the neck.",
    interval_months: 24,
  },
  {
    id: "fh-ckd",
    re: /kidney|\bckd\b|dialysis|polycystic|renal failure/,
    name: "Kidney disease in the family",
    note: "Especially relevant with diabetes/hypertension; polycystic kidney disease is autosomal dominant.",
    actions: "Annual creatinine/eGFR + urine ACR; renal ultrasound once if polycystic disease is suspected.",
    interval_months: 12,
  },
  {
    id: "fh-glaucoma",
    re: /glaucoma/,
    name: "Glaucoma in the family",
    note: "First-degree relatives have up to 9× risk of primary open-angle glaucoma.",
    actions: "Eye-pressure and optic-disc check every 1–2 years after age 40.",
    interval_months: 18,
  },
  {
    id: "fh-tb",
    re: /\btb\b|tuberculosis|koch/,
    name: "Tuberculosis in the household/family",
    note: "Household contacts carry meaningful infection risk, more so if diabetic.",
    actions: "Symptom screen (cough >2 weeks, weight loss, night sweats); low threshold for CXR; ensure the index case completed treatment.",
    interval_months: 6,
  },
  {
    id: "fh-mental",
    re: /depression|suicide|bipolar|schizophren/,
    name: "Psychiatric illness in the family",
    note: "Relevant heritability; also flags family stress context.",
    actions: "Gentle mood screen (PHQ-2); keep threshold low for counselling referral.",
    interval_months: 12,
  },
];

export function familyHistoryInsights(text) {
  const t = (text || "").toLowerCase();
  if (!t.trim()) return [];
  return FH_RULES.filter((r) => r.re.test(t));
}

// Screening-done status for one rule against the patient's screenings_done
// list [{rule_id, done_date}]. satisfied=true → advice is silenced (done and
// not yet due again). overdue=true → was done once but the interval elapsed.
export function screeningStatus(ruleId, doneList) {
  const rec = (doneList || []).find((d) => d && d.rule_id === ruleId && d.done_date);
  if (!rec) return { satisfied: false, done_date: null, due_date: null, overdue: false };
  const rule = FH_RULES.find((r) => r.id === ruleId);
  let due = null;
  if (rule?.interval_months) {
    const d = new Date(rec.done_date);
    if (!isNaN(d.getTime())) {
      d.setMonth(d.getMonth() + rule.interval_months);
      due = d;
    }
  }
  const overdue = due ? Date.now() >= due.getTime() : false;
  return {
    satisfied: !overdue,
    done_date: rec.done_date,
    due_date: due ? due.toISOString().slice(0, 10) : null,
    overdue,
  };
}

// Insights that should still be ADVISED (used by the printed Rx) — excludes
// screenings marked done and not yet due again.
export function activeFhInsights(text, doneList) {
  return familyHistoryInsights(text).filter((r) => !screeningStatus(r.id, doneList).satisfied);
}
